import { NgClass, NgFor, NgIf } from '@angular/common';
import { Component } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { PaymentPlan } from '../../models/enums/payment-plan';
import { PaymentPlanService } from '../../services/payment-plan.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UserResponse } from '../../models/responses/user-response';
import { localStoragevarNames } from '../../utilities/local-storage-utils';
import { RebillingService } from '../../services/rebilling.service';
import { PaymentPlanResponse } from '../../models/responses/payment-plan-response';
import { CustomPaymentPlanComponent } from '../../components/payment/custom-payment-plan/custom-payment-plan.component';
import { MatDialog } from '@angular/material/dialog';
import { DiscountCodeDialogComponent } from '../../components/payment/discount-code-dialog/discount-code-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface PaymentSettings {
  title: string;
  price: number;
  buttonText: string;
  paymentPlan: PaymentPlan;
  subtitle: string;
  costStructure: { minutesIncluded: number, additionalCostsPerMin: number };
  whiteLabel: { whiteLabel: boolean, rebilling: boolean };
  nbAssistants: number;
  actions: { sms: boolean, booking: boolean };
  integrations: { zapier: boolean, cal: boolean };
  support: { whiteGloveOnboarding: boolean, dedicatedSupportManager: boolean, earlyReleaseFeatures: boolean };
  customVoice: { elevenLabs: boolean, playHT: boolean };
}

@Component({
  selector: 'app-payment-plans-page',
  standalone: true,
  imports: [
    NgFor,
    MatDivider,
    MatButtonModule,
    MatIconModule,
    NgClass,
    MatProgressSpinnerModule,
    NgIf,
    CustomPaymentPlanComponent
  ],
  templateUrl: './payment-plans-page.component.html',
  styleUrl: './payment-plans-page.component.scss'
})
export class PaymentPlansPageComponent {
  customPaymentPlans!: PaymentPlanResponse[];
  isCustomPlansLoading: boolean = false;
  paymentPlans: PaymentSettings[] = [
    {
      title: 'Starter plan',
      price: 40,
      buttonText: '',
      subtitle: '',
      paymentPlan: PaymentPlan.STARTER,
      costStructure: {
        minutesIncluded: 150,
        additionalCostsPerMin: 0.30
      },
      whiteLabel: {
        whiteLabel: false,
        rebilling: false
      },
      nbAssistants: 1,
      actions: {
        sms: false,
        booking: false
      },
      integrations: {
        zapier: false,
        cal: false
      },
      support: {
        whiteGloveOnboarding: false,
        dedicatedSupportManager: false,
        earlyReleaseFeatures: false
      },
      customVoice: {
        elevenLabs: true,
        playHT: true
      }
    },
    {
      title: 'Basic plan',
      price: 120,
      buttonText: '',
      subtitle: '',
      paymentPlan: PaymentPlan.BASIC,
      costStructure: {
        minutesIncluded: 600,
        additionalCostsPerMin: 0.15
      },
      whiteLabel: {
        whiteLabel: false,
        rebilling: false
      },
      nbAssistants: 4,
      actions: {
        sms: true,
        booking: true
      },
      integrations: {
        zapier: true,
        cal: true
      },
      support: {
        whiteGloveOnboarding: false,
        dedicatedSupportManager: false,
        earlyReleaseFeatures: false
      },
      customVoice: {
        elevenLabs: true,
        playHT: true
      }
    },
    {
      title: 'Entreprise plan',
      price: 250,
      buttonText: '',
      subtitle: '',
      paymentPlan: PaymentPlan.ENTREPRISE,
      costStructure: {
        minutesIncluded: 2500,
        additionalCostsPerMin: 0.10
      },
      whiteLabel: {
        whiteLabel: false,
        rebilling: false
      },
      nbAssistants: 5,
      actions: {
        sms: true,
        booking: true
      },
      integrations: {
        zapier: true,
        cal: true
      },
      support: {
        whiteGloveOnboarding: true,
        dedicatedSupportManager: true,
        earlyReleaseFeatures: true
      },
      customVoice: {
        elevenLabs: true,
        playHT: true
      }
    },
    {
      title: 'White Labeling plan',
      price: 800,
      buttonText: '',
      subtitle: '',
      paymentPlan: PaymentPlan.WHITELABEL,
      costStructure: {
        minutesIncluded: 3500,
        additionalCostsPerMin: 0.10
      },
      whiteLabel: {
        whiteLabel: true,
        rebilling: true
      },
      nbAssistants: 100,
      actions: {
        sms: true,
        booking: true
      },
      integrations: {
        zapier: true,
        cal: true
      },
      support: {
        whiteGloveOnboarding: true,
        dedicatedSupportManager: true,
        earlyReleaseFeatures: true
      },
      customVoice: {
        elevenLabs: true,
        playHT: true
      }
    },
  ]
  isLoading: boolean[] = [];
  isLoadingForAll: boolean = false;
  connectedUser: UserResponse = JSON.parse(localStorage.getItem(localStoragevarNames.user)!);
  currentPlanString: string = 'Current plan';
  isSubaccount: boolean = false;

  constructor(
    private paymentPlanService: PaymentPlanService,
    private rebillingService: RebillingService,
    private snackBar: MatSnackBar,
    public dialog: MatDialog,
  ) { }

  ngOnInit() {
    this.initButtonText();
    this.setUpCustomPlans();
  }

  setUpCustomPlans() {
    const mainAccountId = this.connectedUser.mainAccountId;
    this.isSubaccount = mainAccountId != undefined;
    if (this.isSubaccount && mainAccountId) {
      this.isCustomPlansLoading = true;

      this.rebillingService.fetchPaymentPlans(mainAccountId).subscribe({
        next: (content) => {
          this.isCustomPlansLoading = false;
          this.customPaymentPlans = content;
        },
        error: () => {
          this.isCustomPlansLoading = false;
          alert('Server error, contact support');
        }
      });
    }
  }

  initButtonText() {
    this.paymentPlans.forEach(plan => {
      plan.buttonText = this.getUserPlanButtonText(plan.paymentPlan);
    })
  }

  onPaymentPlanSelection(plan: PaymentPlan, index: number) {
    this.isLoading[index] = true;
    this.isLoadingForAll = true;
    const userPlan: PaymentPlan | undefined = this.connectedUser.paymentSettings?.paymentPlan;
    
    if (userPlan && this.hasUserPayed()) {
      this.updateSubscription(plan, index);
    } else {
      this.createNewSubscription(plan, index);
    }
  }

  openDiscountCodeDialog() {
    const screenWidth = window.innerWidth;
    let dialogWidth: string;

    if (screenWidth < 600) {
      dialogWidth = '90%';
    } else if (screenWidth < 960) {
      dialogWidth = '70%';
    } else {
      dialogWidth = '50%';
    }

    this.dialog.open(DiscountCodeDialogComponent, {
      width: dialogWidth
    });
  }

  getUserPlanButtonText(plan: PaymentPlan): string {
    const userPlan: PaymentPlan | undefined = this.connectedUser.paymentSettings?.paymentPlan;

    //TODO add further analysis for different cases and ask momo
    if (!userPlan || !this.hasUserPayed()) {
      return 'Select';
    }

    switch (plan) { //TODO add index on all plans and set downgrade/upgrade options 
      case PaymentPlan.BRONZE: {
        if (userPlan == PaymentPlan.BRONZE) {
          return this.currentPlanString;
        } else {
          return 'Downgrade';
        }
      }
      case PaymentPlan.SILVER: {
        if (userPlan == PaymentPlan.SILVER) {
          return this.currentPlanString;
        } else if (userPlan == PaymentPlan.BRONZE) {
          return 'Upgrade';
        } else {
          return 'Downgrade';
        }
      }
      case PaymentPlan.GOLD: {
        if (userPlan == PaymentPlan.GOLD) {
          return this.currentPlanString;
        } else if ([PaymentPlan.BRONZE, PaymentPlan.SILVER].includes(userPlan)) {
          return 'Upgrade';
        } else {
          return 'Downgrade';
        }
      }
      case PaymentPlan.PLATINUM: {
        if (userPlan == PaymentPlan.PLATINUM) {
          return this.currentPlanString;
        } else if ([PaymentPlan.BRONZE, PaymentPlan.SILVER, PaymentPlan.GOLD].includes(userPlan)) {
          return 'Upgrade';
        } else {
          return 'Downgrade';
        }
      }
      case PaymentPlan.ENTREPRISE: {
        if (userPlan == PaymentPlan.ENTREPRISE) {
          return this.currentPlanString;
        } else if ([PaymentPlan.BRONZE, PaymentPlan.SILVER, PaymentPlan.GOLD, PaymentPlan.PLATINUM].includes(userPlan)) {
          return 'Upgrade';
        } else {
          return 'Downgrade';
        }
      }
      case PaymentPlan.WHITELABEL: {
        if (userPlan == PaymentPlan.WHITELABEL) {
          return this.currentPlanString;
        } else if ([PaymentPlan.BRONZE, PaymentPlan.SILVER, PaymentPlan.GOLD, PaymentPlan.PLATINUM, PaymentPlan.ENTREPRISE].includes(userPlan)) {
          return 'Upgrade';
        } else {
          return 'Downgrade';
        }
      }
      default: {
        return 'Select';
      }
    }
  }

  private hasUserPayed(): boolean {
    return this.connectedUser.paymentSettings != undefined && this.connectedUser.paymentSettings.payed;
  }

  private createNewSubscription(plan: PaymentPlan, index: number) {
    this.paymentPlanService.createStripeUrl(plan).subscribe({
      next: (url) => {
        this.subscriptionCallback(index);
        document.location.href = url.url;
      },
      error: () => {
        alert('Server error, contact support');
        this.subscriptionCallback(index);
      }
    });
  }

  private updateSubscription(plan: PaymentPlan, index: number) {
    this.paymentPlanService.updatePlan(plan).subscribe({
      next: () => {
        if (this.connectedUser.paymentSettings) {
          this.connectedUser.paymentSettings.paymentPlan = plan;
          this.initButtonText()
        }

        this.subscriptionCallback(index);
        this.snackBar.open(`Your subscription was updated to ${plan}`, 'Close', { duration: 5000 })
      },
      error: () => {
        alert('Server error, contact support');
        this.subscriptionCallback(index);
      }
    });
  }

  private subscriptionCallback(index: number) {
    this.isLoading[index] = false;
    this.isLoadingForAll = false;
  }
}
